import { LedNumber } from './LedNumber';
import { Flaming7 } from './Flaming7';
import { CasinoButton } from './CasinoButton';
import { brand } from '@/theme';

type PayoutSlot = { place: number; pct: number };

type TournamentCardProps = {
  name: string;
  machineName?: string;
  entryFeeCents: number;
  potCents: number;
  payouts: PayoutSlot[];
  status?: 'setup' | 'active' | 'completed' | 'archived';
  onJoin?: () => void;
};

const placeSuffix = (n: number) => {
  const v = n % 100;
  if (v >= 11 && v <= 13) return `${n}th`;
  switch (n % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
};

const dollars = (cents: number) =>
  (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

export function TournamentCard({
  name,
  machineName = brand.defaultMachineName,
  entryFeeCents,
  potCents,
  payouts,
  status = 'active',
  onJoin,
}: TournamentCardProps) {
  return (
    <div className="cabinet-card overflow-hidden">
      {/* Header — name + flaming 7 mascot */}
      <div className="bg-harlequin relative px-5 pt-5 pb-4">
        <div className="absolute -top-2 -right-2 opacity-90">
          <Flaming7 size={110} />
        </div>
        <p className="font-display uppercase tracking-widest text-cabinet-gold-bright text-xs">
          {machineName}
        </p>
        <h2 className="text-marquee font-display text-3xl md:text-4xl uppercase mt-1 max-w-[80%]">
          {name}
        </h2>
        <span className="mt-2 inline-block rounded-full bg-cabinet-cream/10 px-2.5 py-0.5 text-[10px] uppercase tracking-widest text-cabinet-cream/80 ring-1 ring-cabinet-cream/20">
          {status}
        </span>
      </div>

      {/* Pot + entry fee */}
      <div className="grid grid-cols-2 gap-4 px-5 py-5 border-y border-cabinet-gold/20">
        <div>
          <p className="text-[10px] uppercase tracking-widest text-cabinet-cream/60">Pot</p>
          <div className="mt-1">
            <LedNumber value={Math.round(potCents / 100)} currency digits={5} size="md" />
          </div>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-widest text-cabinet-cream/60">Entry</p>
          <div className="mt-1">
            <LedNumber value={Math.round(entryFeeCents / 100)} currency digits={3} size="md" />
          </div>
        </div>
      </div>

      {/* Paytable */}
      <div className="px-5 py-4">
        <p className="text-[10px] uppercase tracking-widest text-cabinet-cream/60 mb-2">
          Payouts
        </p>
        <ul className="grid grid-cols-3 gap-2">
          {payouts.map((p) => {
            const amount = Math.round((potCents * p.pct) / 100);
            return (
              <li
                key={p.place}
                className="rounded-md bg-cabinet-cream/5 px-3 py-2 ring-1 ring-cabinet-gold/30"
              >
                <p className="font-display text-cabinet-gold text-lg leading-none">
                  {placeSuffix(p.place)}
                </p>
                <p className="font-display text-cabinet-cream text-base mt-0.5">
                  {dollars(amount)}
                </p>
                <p className="text-[10px] text-cabinet-cream/50">{p.pct}%</p>
              </li>
            );
          })}
        </ul>
      </div>

      {/* CTA */}
      <div className="px-5 pb-5 pt-1">
        <CasinoButton fullWidth onClick={onJoin}>
          Join Tournament
        </CasinoButton>
      </div>
    </div>
  );
}
